import React from 'react';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, ChevronRight, FileText } from 'lucide-react';
import '../App.css';

// Public support page. It exists because the Help Center's "Help & Refunds" button
// previously pointed at /help/support, which had no route — the click fell through
// the catch-all and dumped the reader on the homepage.
//
// This has to be PUBLIC: the Account page's Support card covers the same ground but
// sits behind ProtectedRoute, so a logged-out reader following a help link would hit
// a sign-in wall instead of an answer.
//
// Every policy statement below mirrors the Terms of Service. The TOS is the
// authoritative version — if the two ever disagree, fix this page, not the TOS.

const SUPPORT_EMAIL = 'support@trychaptery.com';

const HelpSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const go = (path: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(path); };

  return (
    <>
    <RouteSeo path="/help/support" />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Can I get a refund on my Chaptery subscription?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Annual subscriptions can be refunded in full within 30 days of your initial purchase or most recent renewal. Switching from monthly to annual counts as a purchase: the 30 days run from when you are charged for the annual plan, and a refund returns that charge along with the unused part of your last month. Monthly subscriptions are not refunded, but you can cancel any time before your next billing date and keep access through the end of the period you have paid for. The free trial costs nothing, so there is no payment to refund.',
            },
          },
          {
            '@type': 'Question',
            name: 'What happens when I cancel Chaptery?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Your subscription stops renewing and you keep full access — including the desktop app — through the end of your current billing period. Your writing is never deleted when a subscription lapses, and you can still export it.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do I contact Chaptery support?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Email support@trychaptery.com. We reply within 1 to 2 business days.',
            },
          },
        ],
      })}</script>
    </Helmet>
    <div className="help-page">

      <div className="help-crumbs">
        <a href="/" onClick={go('/')}>Chaptery</a><span className="sep">/</span>
        <a href="/help" onClick={go('/help')}>Help Center</a>
        <span className="sep">/</span><span>Support &amp; refunds</span>
      </div>

      <section className="help-hero">
        <div className="help-hero-inner">
          <span className="eyebrow">Help Center</span>
          <h1>Support &amp; refunds</h1>
          <p>How to reach a human, and exactly how refunds, cancellations and your data work.</p>
        </div>
      </section>

      <div className="help-wrap">

        <section className="help-section">
          <div className="help-section-head"><h2>Talk to us</h2></div>
          <p>
            Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and a person will read it.
            We reply within <strong>1&ndash;2 business days</strong>.
          </p>
          <p>
            It helps to include the email address on your account, which plan you are on, and — for
            anything that went wrong — what you were doing just before it happened.
          </p>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Refunds</h2></div>
          <p>What you are entitled to depends on your plan:</p>
          <ul>
            <li>
              <strong>Annual subscription</strong> — a full refund within <strong>30 days</strong> of your
              initial purchase or your most recent renewal. After 30 days, that renewal is not refundable.
              {' '}Switching from monthly to annual counts as a purchase: the 30 days run from when you are charged for the annual plan, and a refund returns that charge along with the unused part of your last month.
            </li>
            <li>
              <strong>Monthly subscription</strong> — not refunded. You can cancel any time before your next
              billing date and keep access through the end of the period you have already paid for.
            </li>
            <li>
              <strong>Free trial</strong> — costs nothing and needs no card, so there is nothing to refund.
            </li>
          </ul>
          <p>
            To get one, sign in and click <strong>Request a refund</strong> under Plan &amp; Billing on
            your <a href="/account" onClick={go('/account')}>Account page</a>. The button is there while your
            refund window is open. Unlike cancelling, which keeps your access until the end of the period, a
            refund ends it straight away. The refund goes back to your original payment method and usually
            appears within <strong>5&ndash;10 business days</strong>. If something goes wrong, email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from the address on your account.
          </p>
          <p className="help-note">
            The <a href="/tos" onClick={go('/tos')}>Terms of Service</a> are the authoritative version of this
            policy. This page is a plain-language summary.
          </p>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Cancelling</h2></div>
          <p>
            Cancel from your account settings at any time. Your subscription stops renewing, and you keep full
            access — including the desktop app — through the end of your current billing period. Unused time
            is not refunded separately.
          </p>
          <p>
            <strong>Cancelling does not delete anything.</strong> Your books, world building and storyboards
            stay in your account, and you can still export them.
          </p>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Your writing and your data</h2></div>
          <ul>
            <li>
              <strong>Export everything</strong> — from your account page, download a ZIP of every manuscript
              revision plus your world building and storyboards. A fresh archive is built at most once every
              30 days; asking again inside that window returns the most recent one.
            </li>
            <li>
              <strong>Deleting your account</strong> — removes your data from our active systems within about
              30 days, and from our backups within a further 35 days. If you delete by mistake, email us inside
              that 30-day window and we can restore it. After 30 days, deletion is permanent and cannot be reversed.
              Deleting your account also cancels your subscription right away, with no refund for the rest of the
              current period.
            </li>
          </ul>
          <p>
            You own everything you write in Chaptery, during a subscription and after one ends. See{' '}
            <a href="/tos" onClick={go('/tos')}>Terms of Service</a> and{' '}
            <a href="/privacy" onClick={go('/privacy')}>Privacy Policy</a>.
          </p>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Bugs and feature requests</h2></div>
          <ul>
            <li>
              <strong>Something is broken</strong> — report it from inside the desktop app so the report
              carries your app version and logs, which is usually what tells us what went wrong. You can also
              just email us.
            </li>
            <li>
              <strong>Something is missing</strong> — the Chaptery Checklist is our public roadmap. Sign in to
              browse it; submitting a request and voting need an active subscription.
            </li>
          </ul>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Related</h2></div>
          <ul className="help-popular-list">
            <li>
              <a href="/help/article/account-billing" onClick={go('/help/article/account-billing')}>
                <span className="pop-icon"><FileText size={17} /></span>
                Account &amp; billing
                <span className="pop-arrow"><ChevronRight size={16} /></span>
              </a>
            </li>
            <li>
              <a href="/help" onClick={go('/help')}>
                <span className="pop-icon"><FileText size={17} /></span>
                Browse the Help Center
                <span className="pop-arrow"><ChevronRight size={16} /></span>
              </a>
            </li>
          </ul>
        </section>

        <section className="help-contact">
          <h3>Still stuck?</h3>
          <p>Send us the details and we will work it out with you.</p>
          <div className="help-contact-actions">
            <a
              className="btn-primary"
              href={`mailto:${SUPPORT_EMAIL}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px' }}
            >
              <Mail size={15} /> Email support
            </a>
            <a
              className="btn-secondary"
              href="/help"
              onClick={go('/help')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px' }}
            >
              <ArrowLeft size={15} /> Back to Help Center
            </a>
          </div>
        </section>

      </div>
    </div>
    </>
  );
};

export default HelpSupportPage;
